import { useEffect, useState } from "react";
import axios from "axios";
import {
  Clock,
  RefreshCw,
  AlertTriangle,
  Info,
  Activity,
  Package,
  TrendingUp,
  Shield,
  Sparkles,
} from "lucide-react";
import { LocationIndicator } from "./LocationIndicator";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section D de la Veille Proactive — Synthèse de tension sanitaire.
 *
 * Pilier final qui agrège les sections A/B/C en un score composite 0-100 et un
 * hook narratif Cabinet (vouvoiement, ton D+). Branchée sur l'endpoint
 * `GET /api/veille-proactive/synthese-composite` (étape 6.5).
 *
 * Composition visuelle :
 *  1. En-tête de section + tonalité du cycle (zone + cached_at + pondération)
 *  2. Hook Cabinet narratif (centre de la section, Playfair, citation noble)
 *  3. Jauge composite circulaire + libellé du niveau
 *  4. Trois piliers compacts : Épidémiologie / Ruptures / Tendances
 *
 * 5 états : loading, live, partial, empty (serein), unavailable.
 */
export function SyntheseSection() {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("benchory_token");
      const resp = await axios.get(`${API_URL}/veille-proactive/synthese-composite`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 25000,
      });
      setPayload(resp.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[veille-proactive] Synthèse fetch error:", e?.message);
      setPayload({
        data: {
          composite_score: null,
          composite_level: null,
          pillars: {},
          hook: null,
          location: {},
        },
        meta: { status: "unavailable", user_message: null },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      className="space-y-6"
      data-testid="synthese-section"
      aria-labelledby="synthese-title"
    >
      <SectionHeader
        meta={payload?.meta}
        location={payload?.data?.location}
        loading={loading}
        onRefresh={() => {
          setLoading(true);
          fetchData();
        }}
      />

      {loading && <SyntheseSkeleton />}

      {!loading && payload && (
        <SyntheseContent data={payload.data || {}} meta={payload.meta || {}} />
      )}
    </section>
  );
}


// ── En-tête ───────────────────────────────────────────────────────────────────


function SectionHeader({ meta, location, loading, onRefresh }) {
  const isStale = _isOld(meta?.cached_at);
  const unavailableAxes = meta?.unavailable_axes || [];

  return (
    <header className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Shield size={14} className="text-cabinet-bordeaux" strokeWidth={1.7} />
            <span className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-cabinet-mute-1">
              Synthèse Cabinet · Vue consolidée
            </span>
          </div>
          <h2
            id="synthese-title"
            className="font-playfair text-2xl lg:text-3xl text-cabinet-ink font-medium tracking-tight"
          >
            Synthèse de tension sanitaire
          </h2>
          <p className="mt-1.5 text-cabinet-navy text-[14px] leading-relaxed max-w-2xl">
            Lecture composite des trois axes de la veille — épidémiologie, ruptures
            et signaux territoriaux — pondérés et traduits en une tonalité unique
            pour orienter votre semaine d'un coup d'œil.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex-shrink-0 text-cabinet-mute-1 hover:text-cabinet-bordeaux transition-colors p-2 rounded-md disabled:opacity-40"
          data-testid="synthese-refresh"
          aria-label="Actualiser la synthèse composite"
        >
          <RefreshCw size={16} strokeWidth={1.8} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {location?.dept && <LocationIndicator location={location} />}
        {meta?.weights_applied && Object.keys(meta.weights_applied).length > 0 && (
          <WeightsTooltip
            weights={meta.weights_applied}
            defaultWeights={meta.weights_default}
          />
        )}
        {meta?.cached_at && (
          <span
            className="inline-flex items-center gap-1.5 text-[11.5px] tracking-wide text-cabinet-mute-1"
            data-testid="synthese-cached-at"
          >
            <Clock size={11} strokeWidth={1.8} />
            {isStale ? "Données du" : "Mis à jour"} {_formatDateTime(meta.cached_at)}
          </span>
        )}
      </div>

      {unavailableAxes.length > 0 && meta?.user_message && (
        <div
          className="inline-flex items-start gap-2 px-3.5 py-2 rounded-md bg-cabinet-cream-warm border border-cabinet-gold/30 text-[12.5px] text-cabinet-ink max-w-2xl"
          data-testid="synthese-partial-banner"
        >
          <Info size={13} className="flex-shrink-0 mt-0.5 text-cabinet-gold" strokeWidth={1.8} />
          <span>{meta.user_message}</span>
        </div>
      )}
    </header>
  );
}


function WeightsTooltip({ weights, defaultWeights }) {
  const lines = Object.entries(weights || {})
    .map(([k, v]) => `${_axisLabel(k)} : ${Math.round((v || 0) * 100)} %`)
    .join("\n");
  const def = defaultWeights
    ? `\n\nCible documentée :\n${Object.entries(defaultWeights)
        .map(([k, v]) => `${_axisLabel(k)} : ${Math.round((v || 0) * 100)} %`)
        .join("\n")}`
    : "";
  return (
    <span
      className="inline-flex items-center gap-1 text-[11.5px] tracking-wide text-cabinet-mute-1 cursor-help"
      title={`Pondération appliquée :\n${lines}${def}`}
      data-testid="synthese-weights-tooltip"
    >
      <Info size={11} strokeWidth={1.8} />
      Pondération composite
    </span>
  );
}


// ── Skeleton ──────────────────────────────────────────────────────────────────


function SyntheseSkeleton() {
  return (
    <div className="space-y-6" data-testid="synthese-skeleton">
      {/* Hook + gauge skeleton */}
      <div className="bg-white border border-cabinet-mute-2/40 rounded-lg p-8 lg:p-10 animate-pulse">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2 space-y-3">
            <div className="h-4 w-1/4 rounded bg-cabinet-mute-2/40" />
            <div className="h-5 rounded bg-cabinet-mute-2/50" />
            <div className="h-5 w-4/5 rounded bg-cabinet-mute-2/50" />
            <div className="h-5 w-3/5 rounded bg-cabinet-mute-2/50" />
          </div>
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="w-36 h-36 rounded-full bg-cabinet-mute-2/40" />
            <div className="h-4 w-2/3 rounded bg-cabinet-mute-2/50" />
          </div>
        </div>
      </div>
      {/* Pillars skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="bg-white border border-cabinet-mute-2/40 rounded-lg p-5 animate-pulse"
          >
            <div className="h-3 w-1/3 rounded bg-cabinet-mute-2/40 mb-2" />
            <div className="h-5 w-1/2 rounded bg-cabinet-mute-2/55 mb-4" />
            <div className="h-2 w-full rounded bg-cabinet-mute-2/40 mb-3" />
            <div className="h-3 rounded bg-cabinet-mute-2/30" />
            <div className="h-3 w-4/5 rounded bg-cabinet-mute-2/30 mt-2" />
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Contenu : 3 états principaux ─────────────────────────────────────────────


function SyntheseContent({ data, meta }) {
  const status = meta?.status;
  const composite = data?.composite_score;
  const level = data?.composite_level;
  const pillars = data?.pillars || {};

  if (status === "unavailable") {
    return (
      <PremiumEmptyState
        icon={AlertTriangle}
        title="La synthèse composite n'est pas consolidable ce cycle"
        message="Nos trois sources amont (épidémiologie, ruptures, tendances) sont momentanément indisponibles. La collecte reprend automatiquement à la prochaine actualisation, vos autres modules Benchory restent fiables."
        testId="synthese-empty-unavailable"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Pavé central : hook + jauge */}
      <article
        className="bg-cabinet-cream-warm border border-cabinet-mute-2/45 rounded-lg p-8 lg:p-10"
        data-testid="synthese-pavillon-central"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-2">
            <Hook
              hook={data?.hook}
              level={level}
              status={status}
            />
          </div>
          <div className="flex flex-col items-center justify-center">
            <CompositeGauge score={composite} level={level} />
            <LevelChip level={level} score={composite} />
          </div>
        </div>
      </article>

      {/* Trois piliers compacts */}
      <div
        className="grid grid-cols-1 lg:grid-cols-3 gap-4"
        data-testid="synthese-pillars-grid"
      >
        <PillarCard
          axis="epidemiologie"
          pillar={pillars.epidemiologie}
        />
        <PillarCard
          axis="ruptures"
          pillar={pillars.ruptures}
        />
        <PillarCard
          axis="tendances"
          pillar={pillars.tendances}
        />
      </div>
    </div>
  );
}


// ── Hook narratif Cabinet ────────────────────────────────────────────────────


function Hook({ hook, level, status }) {
  if (!hook) {
    return (
      <p
        className="font-playfair text-cabinet-ink text-[19px] lg:text-[21px] leading-[1.6] tracking-tight italic"
        data-testid="synthese-hook"
      >
        Nous consolidons la lecture de votre semaine.
      </p>
    );
  }
  return (
    <div data-testid="synthese-hook-wrapper">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={12} className="text-cabinet-bordeaux" strokeWidth={1.7} />
        <span className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-cabinet-mute-1">
          Lecture du cycle · {_levelLabel(level, status)}
        </span>
      </div>
      <blockquote
        className="font-playfair text-cabinet-ink text-[19px] lg:text-[21px] leading-[1.55] tracking-tight font-normal"
        data-testid="synthese-hook"
      >
        {hook}
      </blockquote>
    </div>
  );
}


// ── Jauge composite circulaire ───────────────────────────────────────────────


function CompositeGauge({ score, level }) {
  // Cercle SVG avec arc proportionnel au score. Plage utile = demi-cercle haut + bas
  // d'un cercle complet (360°) pour une lecture "dial" complète.
  const size = 160;
  const stroke = 9;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safeScore = typeof score === "number" && !Number.isNaN(score) ? Math.max(0, Math.min(100, score)) : 0;
  const filled = circumference * (safeScore / 100);
  const accent = _levelAccent(level);

  return (
    <div
      className="relative"
      style={{ width: size, height: size }}
      data-testid="synthese-gauge"
      role="img"
      aria-label={`Score composite ${typeof score === "number" ? score.toFixed(0) : "indisponible"} sur 100`}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E5DED1"
          strokeWidth={stroke}
          fill="none"
        />
        {typeof score === "number" && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={accent}
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={`${filled} ${circumference - filled}`}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 0.6s ease-out" }}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {typeof score === "number" ? (
          <>
            <span
              className="font-playfair text-[44px] leading-none text-cabinet-ink tracking-tight font-medium tabular-nums"
              data-testid="synthese-score-value"
            >
              {Math.round(score)}
            </span>
            <span className="mt-1 text-[10.5px] uppercase tracking-[0.18em] text-cabinet-mute-1 font-medium">
              / 100
            </span>
          </>
        ) : (
          <span
            className="font-playfair text-[24px] leading-none text-cabinet-mute-1 italic font-normal text-center px-2"
            data-testid="synthese-score-unavailable"
          >
            indispo&shy;nible
          </span>
        )}
      </div>
    </div>
  );
}


function LevelChip({ level, score }) {
  if (!level || typeof score !== "number") {
    return null;
  }
  const accent = _levelAccent(level);
  return (
    <span
      className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold"
      style={{
        color: accent,
        backgroundColor: `${accent}14`,
        border: `1px solid ${accent}33`,
      }}
      data-testid="synthese-level-chip"
    >
      {_levelLabel(level)}
    </span>
  );
}


// ── Pilier (carte par axe) ───────────────────────────────────────────────────


function PillarCard({ axis, pillar }) {
  const icon = _axisIcon(axis);
  const Icon = icon;
  const available = pillar?.available;
  const score = pillar?.score;
  const level = pillar?.level;
  const headline = pillar?.headline;
  const accent = available ? _levelAccent(level) : "#8E8579";

  return (
    <article
      className="bg-white border border-cabinet-mute-2/40 rounded-lg p-5 flex flex-col"
      data-testid={`synthese-pillar-${axis}`}
    >
      <header className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon size={12} className="text-cabinet-bordeaux/70" strokeWidth={1.8} />
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-cabinet-mute-1">
            Pilier · {_axisLabel(axis)}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-playfair text-[17px] text-cabinet-ink font-medium tracking-tight">
            {_axisTitle(axis)}
          </h3>
          {available && typeof score === "number" ? (
            <span
              className="font-playfair text-[20px] tracking-tight tabular-nums"
              style={{ color: accent }}
              data-testid={`synthese-pillar-score-${axis}`}
            >
              {Math.round(score)}
            </span>
          ) : (
            <span className="text-[11px] uppercase tracking-[0.15em] text-cabinet-mute-1 italic">
              hors-ligne
            </span>
          )}
        </div>
      </header>

      <PillarBar score={available ? score : null} accent={accent} />

      <div className="flex items-center justify-between mt-3 mb-2">
        {available && level ? (
          <span
            className="text-[11px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: accent }}
          >
            {_levelLabel(level)}
          </span>
        ) : (
          <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-cabinet-mute-1">
            source HS
          </span>
        )}
      </div>

      <p className="text-[12.5px] leading-relaxed text-cabinet-navy">
        {headline || "Aucune information disponible pour le moment."}
      </p>
    </article>
  );
}


function PillarBar({ score, accent }) {
  const filled = typeof score === "number" ? Math.max(0, Math.min(100, score)) : 0;
  return (
    <div className="h-1.5 w-full rounded-sm bg-cabinet-mute-2/40 overflow-hidden">
      <div
        className="h-full rounded-sm"
        style={{
          width: `${filled}%`,
          backgroundColor: typeof score === "number" ? accent : "transparent",
          transition: "width 0.6s ease-out",
        }}
      />
    </div>
  );
}


// ── État vide premium ────────────────────────────────────────────────────────


function PremiumEmptyState({ icon: Icon, title, message, testId }) {
  return (
    <div
      className="bg-cabinet-cream-warm border border-cabinet-mute-2/40 rounded-lg px-8 py-12 text-center"
      data-testid={testId}
    >
      <div className="inline-flex w-12 h-12 rounded-full bg-cabinet-bordeaux/8 items-center justify-center mb-4">
        <Icon size={20} className="text-cabinet-bordeaux" strokeWidth={1.5} />
      </div>
      <h3 className="font-playfair text-xl text-cabinet-ink font-medium tracking-tight">
        {title}
      </h3>
      <p className="mt-2.5 text-cabinet-navy text-[14px] leading-relaxed max-w-md mx-auto">
        {message}
      </p>
    </div>
  );
}


// ── Helpers ──────────────────────────────────────────────────────────────────


function _axisIcon(axis) {
  return {
    epidemiologie: Activity,
    ruptures: Package,
    tendances: TrendingUp,
  }[axis] || Activity;
}


function _axisLabel(axis) {
  return {
    epidemiologie: "épidémiologie",
    ruptures: "ruptures",
    tendances: "tendances",
  }[axis] || axis;
}


function _axisTitle(axis) {
  return {
    epidemiologie: "Épidémiologie locale",
    ruptures: "Pression de rupture",
    tendances: "Signaux territoriaux",
  }[axis] || axis;
}


function _levelLabel(level) {
  return {
    serein: "Cycle serein",
    vigilance: "Vigilance",
    tension: "Tension",
    pression_forte: "Pression forte",
  }[level] || "Lecture en cours";
}


function _levelAccent(level) {
  // Tokens Cabinet en hex pour usage SVG/inline-style.
  return {
    serein: "#4A6B3A",         // forest — calme noble
    vigilance: "#B5862A",      // gold — attention sobre
    tension: "#A6334C",        // bordeaux-soft — tension marquée
    pression_forte: "#8B0028", // bordeaux profond — pression élevée
  }[level] || "#8E8579";       // mute-1 — neutre / hors-ligne
}


function _formatDateTime(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const datePart = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
    }).format(d);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${datePart} à ${hh}h${mm}`;
  } catch {
    return iso;
  }
}


function _isOld(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return false;
    return (Date.now() - d.getTime()) / (1000 * 60 * 60) > 24;
  } catch {
    return false;
  }
}


export default SyntheseSection;
